import { merge } from 'lodash';
import { defaultTheme, RaThemeOptions } from 'react-admin';
import { match } from 'ts-pattern';

import { axaThemeCustomizations } from '@sh4res/bo-common/theme/axaTheme';
import { mlkThemeCustomizations } from '@sh4res/bo-common/theme/mlkTheme';
import {
  isAxaEnv,
  isBoExternal,
  isDevEnv,
  isLocalEnv,
  isMlkEnv,
  isStgEnv,
} from '@sh4res/bo-common/utils/env';

export const themeCommon: RaThemeOptions = {
  ...defaultTheme,
  palette: {
    ...defaultTheme.palette,
    primary: {
      main: '#594FF4',
    },
    secondary: {
      main: '#444444',
    },
    info: {
      main: '#E8E8E8',
    },
  },
  typography: {
    ...defaultTheme.typography,
    fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif',
    fontSize: 13,
  },
  components: {
    ...defaultTheme.components,

    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: 'rgba(0,0,0,0.6)',
            opacity: 1,
          },

          '&.Mui-disabled': {
            color: 'rgba(0,0,0,0.55)',
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color: 'rgba(0,0,0,0.55)',
          },
        },
      },
    },
    // Hide the zero-width space react-admin renders to reserve helper text space under every input.
    // Only that span has no class: keeps MUI's own `helperText=" "` span and styled spans in helper texts
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          '& > span:not([class])': {
            display: 'none',
          },
        },
      },
    },
    MuiInputAdornment: {
      styleOverrides: {
        root: {
          '& .MuiSvgIcon-root.Mui-disabled': {
            fill: 'rgba(0,0,0,0.55)',
          },
        },
      },
    },

    /* React-Admin overrides */
    RaSidebar: {
      styleOverrides: {
        root: {
          backgroundColor: 'black',
          '& .RaSidebar-paper': {
            transition: 'none',
            webkitTransition: 'none',
            height: 'var(--app-height)',
          },
          '& .RaSidebar-fixed': {
            overflow: 'hidden',
          },
        },
      },
    },
    RaSidebarToggleButton: {
      styleOverrides: {
        root: {
          color: 'white',
          marginTop: '5px',
        },
      },
    },
    RaLayout: {
      styleOverrides: {
        root: {
          '& .RaLayout-content': {
            overflow: 'auto',
            paddingLeft: '20px',
            paddingRight: '20px',
            height: 'calc(var(--app-height) - 60px)',
            width: 'calc(100vw - 240px)',
            overflowAnchor: 'none',
            zIndex: 1100,
          },
          '& .RaLayout-appFrame': {
            marginTop: '60px',
          },
        },
      },
    },
    RaMenuItem: {
      styleOverrides: {
        root: {
          color: '#808080',
          height: '40px',
          '& .RaMenuItem-active': {
            height: '40px',
            color: 'white',
            backgroundColor: '#282828',
            borderRadius: '9px',
            paddingLeft: '10px',
            marginLeft: '-10px',
          },
          '& .RaMenuItem-menuIcon': {
            minWidth: '33px',
          },
          '& .MuiListItemText-root': {
            marginTop: '4px',
            marginBottom: '4px',
          },
          '& .MuiListItemText-primary': {
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          },
          '& .MuiTypography-root': {
            fontWeight: 500,
            fontSize: '15.5px',
          },
          '& .MuiListItem-root': {
            height: '40px',
          },
        },
      },
    },
    RaShow: {
      styleOverrides: {
        root: {
          display: 'grid',
        },
      },
    },
    RaFilterForm: {
      styleOverrides: {
        root: {
          display: 'contents',
        },
      },
    },
    RaBulkActionsToolbar: {
      styleOverrides: {
        root: {
          '& .RaBulkActionsToolbar-toolbar': {
            position: 'fixed',
            left: 'auto',
            right: 'auto',
          },
        },
      },
    },
    RaTopToolbar: {
      styleOverrides: {
        root: {
          paddingBottom: '10px',
        },
      },
    },
    RaListToolbar: {
      styleOverrides: {
        root: {
          '& button': { textTransform: 'uppercase !important' },
        },
      },
    },

    /* Material UI overrides */
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'transparent',
          boxShadow: 'none',
          color: 'black',
          height: '60px',
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          width: '100%',
          justifyContent: 'left !important',
          '& form': {
            flex: 'initial',
          },
        },
      },
    },
    MuiDialogTitle: {
      ...defaultTheme.components?.MuiDialogTitle,
      styleOverrides: {
        root: {
          position: 'sticky',
          top: 0,
          margin: 0,
          background: 'white',
          zIndex: 10,
          borderBottom: '#E0E0E0',
          borderBottomStyle: 'solid',
          borderWidth: '1px',
        },
      },
    },
    MuiFormControl: {
      ...defaultTheme.components?.MuiFormControl,
      styleOverrides: {
        root: {
          width: '256px',
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        margin: '5px',
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingTop: '5px',
          paddingBottom: '5px',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '19px',
          boxShadow: 'none',
          textTransform: 'capitalize',
          marginRight: '16px !important',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          height: '28px',
          backgroundColor: '#16002C0D',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          '& .Mui-selected': {
            color: 'black !important',
          },
        },
      },
    },
  },
};

export function getTheme(): 'default' | 'axa' | 'mlk' {
  if (isMlkEnv()) {
    return 'mlk';
  }
  if (isAxaEnv() || (isBoExternal() && (isLocalEnv() || isDevEnv() || isStgEnv()))) {
    return 'axa';
  }
  return 'default';
}

// Excel templates are AXA-branded by default; Malakoff environments ship an MH-branded variant.
export function getBrandedFile(file: string, mlkFile: string): string {
  return getTheme() === 'mlk' ? mlkFile : file;
}

/**
 * Returns the appropriate theme based on the current environment.
 * For Axa environments, returns merged theme with Axa customizations.
 * For standard environments, returns the default themeCommon.
 */
export function getThemeForEnvironment(): RaThemeOptions {
  return merge(
    themeCommon,
    match(getTheme())
      .with('axa', () => axaThemeCustomizations)
      .with('mlk', () => mlkThemeCustomizations)
      .with('default', () => ({}))
      .exhaustive(),
  );
}
